/**
 * Presentation helpers for the Kill Race pages.
 *
 * Two things worth knowing about the shape of the data these deal with:
 *
 * 1. Money and ratios arrive as **strings**. Django's `JsonResponse` encodes
 *    `Decimal` with `DjangoJSONEncoder`, which stringifies rather than risking
 *    float precision — so `prize`, `earnings` and `potential_reward` are
 *    `"200.00"`, not `200`. Never do arithmetic on them without `toNumber`.
 *
 * 2. Countdowns are derived from the `begin`/`end` timestamps rather than the
 *    `remaining_seconds` the API sends. The serializer builds that field from
 *    `timedelta.seconds`, which is the *sub-day* remainder — a race with two
 *    days left reports the same number as one with two hours left. The ISO
 *    timestamps are unambiguous, so we count down from those.
 */

export type Phase = 'Upcoming' | 'Live' | 'Completed' | 'Disabled'

export const toNumber = (value: unknown): number => {
    const parsed = Number(value ?? 0)
    return Number.isFinite(parsed) ? parsed : 0
}

export const formatUsd = (value: unknown, decimals = 2) =>
    `$${toNumber(value).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`

/** Whole seconds between now and `iso`, floored at 0. `null` when unparseable. */
export const secondsUntil = (iso?: string | null): number | null => {
    if (!iso) return null
    const target = new Date(iso).getTime()
    if (Number.isNaN(target)) return null
    return Math.max(0, Math.floor((target - Date.now()) / 1000))
}

/**
 * The list endpoint sends a `phase` label; the detail endpoint does not.
 * Fall back to deriving it from the window so both pages can badge alike.
 */
export const phaseOf = (begin?: string, end?: string): Phase => {
    const now = Date.now()
    if (begin && new Date(begin).getTime() > now) return 'Upcoming'
    if (end && new Date(end).getTime() < now) return 'Completed'
    return 'Live'
}

export const resolvePhase = (phase: string | undefined, begin?: string, end?: string): Phase => {
    if (phase === 'Live' || phase === 'Upcoming' || phase === 'Completed' || phase === 'Disabled') return phase
    return phaseOf(begin, end)
}

/**
 * Coarse "how long until", for chrome that must not draw the eye — `2d`, `3h`, `12m`.
 *
 * Deliberately not a `Countdown`: this reads inside tab labels, and a ticking
 * seconds figure there is movement in the one part of the page that should hold
 * still while you decide what to click.
 */
export const coarseDuration = (seconds: number): string => {
    if (seconds <= 0) return 'now'
    const days = Math.floor(seconds / 86400)
    if (days > 0) return `${days}d`
    const hours = Math.floor(seconds / 3600)
    if (hours > 0) return `${hours}h`
    const minutes = Math.floor(seconds / 60)
    if (minutes > 0) return `${minutes}m`
    return `${seconds}s`
}

export const formatDateTime = (iso?: string | null) => {
    if (!iso) return '—'
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export const formatDate = (iso?: string | null) => {
    if (!iso) return '—'
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

export const formatTime = (iso?: string | null) => {
    if (!iso) return ''
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/**
 * The clock span of a race, given its start date is already shown alongside.
 * A 24-hour race reads "11:41 AM — 11:41 AM" if you only print times, so the
 * end date is repeated whenever the race crosses midnight.
 */
export const formatSpan = (begin?: string | null, end?: string | null) => {
    if (!begin || !end) return '—'
    const from = new Date(begin)
    const to = new Date(end)
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '—'
    return from.toDateString() === to.toDateString()
        ? `${formatTime(begin)} to ${formatTime(end)}`
        : `${formatTime(begin)} to ${formatDate(end)} ${formatTime(end)}`
}

/**
 * Ranks 1-3 get their metal; everything else stays neutral.
 *
 * `hex` is here because the medal colour is also applied as an inline border
 * colour. Tailwind can express it, but `border-l-<colour>` and
 * `border-<colour>` are separate merge groups, so a conditional class ends up
 * racing a base class of equal specificity. An inline style just wins.
 */
export const rankTone = (rank?: number) =>
    ({
        1: { text: 'text-[#FFD600]', border: 'border-[#FFD600]/50', hex: '#FFD600' },
        2: { text: 'text-[#D6D6D6]', border: 'border-[#D6D6D6]/50', hex: '#D6D6D6' },
        3: { text: 'text-[#E08B3E]', border: 'border-[#E08B3E]/50', hex: '#E08B3E' },
    }[rank] ?? { text: 'text-white/70', border: 'border-white/15', hex: 'transparent' })
